import { useEffect, useRef, useState } from 'react';

const highlights = [
  { icon: 'ri-heart-line', text: 'Atendimento humanizado e próximo ao cliente' },
  { icon: 'ri-eye-line', text: 'Transparência total em todas as etapas do processo' },
  { icon: 'ri-award-line', text: 'Especialização profunda em Direito Trabalhista' },
];

export default function AboutSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="sobre" className="bg-brand-light py-20 md:py-28 px-4 md:px-8">
      <div
        ref={ref}
        className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
      >
        {/* Text Column */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(-40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <span className="text-brand-gold font-sans text-xs font-semibold tracking-widest uppercase">Sobre Nós</span>
          <h2 className="font-serif text-brand-black text-3xl md:text-5xl font-bold mt-3 leading-tight">
            Um escritório que<br />
            <em className="not-italic text-brand-green">cuida de você</em>
          </h2>
          <p className="font-sans text-brand-green/80 text-base md:text-lg leading-relaxed mt-6">
            A <strong>Bertochi e Lemes Sociedade de Advogados</strong> nasceu com um propósito claro: defender trabalhadores que tiveram seus direitos violados. Somos especialistas em acidente de trabalho e doença ocupacional, atuando com profundidade técnica e sensibilidade humana.
          </p>
          <p className="font-sans text-brand-green/70 text-base leading-relaxed mt-4">
            Entendemos que por trás de cada processo existe uma pessoa que sofreu, que perdeu sua capacidade de trabalhar, que enfrenta incertezas. Por isso, nosso atendimento vai além do jurídico — oferecemos suporte, clareza e estratégia em cada etapa.
          </p>
          <p className="font-sans text-brand-green/70 text-base leading-relaxed mt-4">
            Atendemos online em toda a região de São Paulo, ABC Paulista e Campinas, com horários flexíveis e comunicação direta.
          </p>

          <ul className="mt-8 space-y-4">
            {highlights.map((h, i) => (
              <li key={i} className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-gold/10 border border-brand-gold/30 flex-shrink-0">
                  <i className={`${h.icon} text-brand-gold text-base`} />
                </div>
                <span className="font-sans text-brand-green text-sm md:text-base font-medium">{h.text}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex items-center gap-6">
            <div className="text-center">
              <p className="font-serif text-brand-black text-3xl font-bold">+500</p>
              <p className="font-sans text-brand-green/60 text-xs mt-1">Casos atendidos</p>
            </div>
            <div className="w-px h-12 bg-brand-gold/30" />
            <div className="text-center">
              <p className="font-serif text-brand-black text-3xl font-bold">98%</p>
              <p className="font-sans text-brand-green/60 text-xs mt-1">Clientes satisfeitos</p>
            </div>

          </div>
        </div>

        {/* Images Column */}
        <div
          className="relative hidden lg:flex items-end gap-5"
          style={{
            height: '520px',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(40px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          {/* Mirella Bertochi — foto maior, alinhada ao topo */}
          <div className="flex flex-col items-center gap-3 flex-1" style={{ marginBottom: '60px' }}>
            <div className="w-full rounded-2xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/8f779ffa-1a3c-4c5e-bbb9-023ea8838f97/14527c1b-e69b-42e4-8862-3d59570ae283_bertochi-e-lemes.jpg?v=3a13f287b6f201830fe828e9c4a7c70b"
                alt="Mirella Bertochi"
                className="w-full h-auto block"
                style={{ imageRendering: 'auto' }}
              />
            </div>
            <div className="text-center">
              <p className="font-serif text-brand-black text-lg font-bold">Mirella Bertochi</p>
              <p className="font-sans text-brand-green/60 text-xs mt-0.5 tracking-wide uppercase">Advogada</p>
            </div>
          </div>

          {/* Linha decorativa dourada */}
          <div className="w-px self-stretch bg-brand-gold/25 flex-shrink-0" />

          {/* Bianca Lemes — foto menor, alinhada à base */}
          <div className="flex flex-col items-center gap-3 flex-1">
            <div className="w-full rounded-2xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/8f779ffa-1a3c-4c5e-bbb9-023ea8838f97/9a8082c6-5cb0-48d5-9025-cf555179e5e3_bianca-lemes.png?v=fb3e80f5a2565a7560cbc414e00cb0ba"
                alt="Bianca Lemes"
                className="w-full h-auto block"
                style={{ imageRendering: 'auto' }}
              />
            </div>
            <div className="text-center">
              <p className="font-serif text-brand-black text-lg font-bold">Bianca Lemes</p>
              <p className="font-sans text-brand-green/60 text-xs mt-0.5 tracking-wide uppercase">Advogada</p>
            </div>
          </div>
        </div>

        {/* Mobile layout — empilhado */}
        <div
          className="flex flex-col gap-6 lg:hidden"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="w-full rounded-2xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/8f779ffa-1a3c-4c5e-bbb9-023ea8838f97/14527c1b-e69b-42e4-8862-3d59570ae283_bertochi-e-lemes.jpg?v=3a13f287b6f201830fe828e9c4a7c70b"
                alt="Mirella Bertochi"
                className="w-full h-auto block"
              />
            </div>
            <div className="text-center">
              <p className="font-serif text-brand-black text-lg font-bold">Mirella Bertochi</p>
              <p className="font-sans text-brand-green/60 text-xs mt-0.5 tracking-wide uppercase">Advogada</p>
            </div>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="w-full rounded-2xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/8f779ffa-1a3c-4c5e-bbb9-023ea8838f97/9a8082c6-5cb0-48d5-9025-cf555179e5e3_bianca-lemes.png?v=fb3e80f5a2565a7560cbc414e00cb0ba"
                alt="Bianca Lemes"
                className="w-full h-auto block"
              />
            </div>
            <div className="text-center">
              <p className="font-serif text-brand-black text-lg font-bold">Bianca Lemes</p>
              <p className="font-sans text-brand-green/60 text-xs mt-0.5 tracking-wide uppercase">Advogada</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
